import {useApiariesStore} from 'entities/apiaries';
import {useCalendarStore} from 'entities/calendar/model/store';
import {useDiaryStore} from 'entities/diary';
import {useDictionariesStore} from 'entities/dictionaries';
import {useHivesStore} from 'entities/hives';
import {useNotificationState} from 'entities/notifications';
import {useUserStore} from 'entities/user';
import {applyTheme, useThemeStore} from 'entities/theme';
import {useWeatherStore} from 'entities/weather';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {ConfigContext} from '../../App.tsx';
import {usePesticideMapStore} from 'entities/pesticideMap';
import {useHivesSoundsStore} from 'entities/hiveSound';
import {useMercuryStore} from 'entities/mercury';
import {useVetisStore} from 'entities/vetis';
import axios from 'axios';
import {useTimezonesStore} from 'entities/timezones/model/store.ts';
import {getNowForUserTz} from 'features/calendar/helpers/dates.ts';
import {logUnsupportedBrowser} from 'shared/lib/core/logUnssuportedBrowser.ts';
import {useDevicesStore} from 'entities/devices';

export const useAppInit = () => {
  const navigate = useNavigate();

  // const [ isInitApp, setIsInitApp ] = useState<boolean>(false);
  const isInitApp = useRef(false);
  const isInitAppStart = useRef(false);

  const [isInitReturned, setIsInitReturned] = useState<boolean>(false);
  const [yandexMapsScript, setYandexMapsScript] = useState<HTMLScriptElement | null>(null);

  const config = useContext(ConfigContext);

  const origin = window.location.origin || config.VITE_AUTH_HOST;
  const pathname = window.location.pathname || '/';
  const myHost = origin + pathname;
  const authorizePath = useRef<string>(
    `/ecpp/oauth/realms/main/authorize?response_type=code&client_id=web&redirect_uri=${myHost}&frontend_url=${window.location.origin}`
  );
  const lastPathName = localStorage.getItem('lastPathName') || '/';

  const getUserInfo = useUserStore((state) => state.getUserInfo);
  const getMyInfo = useUserStore((state) => state.getMyInfo);
  const getUsers = useUserStore((state) => state.getUsers);
  const setIAmAdmin = useUserStore((state) => state.setIAmAdmin);

  const theme = useThemeStore((state) => state.theme);

  const currentApiary = useApiariesStore((s) => s.currentApiary());
  const getApiaries = useApiariesStore((state) => state.getApiaries);
  const getCurrentApiaryId = useApiariesStore((state) => state.getCurrentApiaryId);
  const getShareOffers = useApiariesStore((state) => state.getShareOffers);

  const fetchForecast = useWeatherStore((s) => s.fetchForecast);
  const setCoords = useWeatherStore((s) => s.setCoords);

  const getDictionariesFromServer = useDictionariesStore(
    (state) => state.getDictionariesFromServer
  );

  const getHivesFromServer = useHivesStore((state) => state.getHivesFromServer);
  const getSharedHives = useHivesStore((state) => state.getSharedHives);

  const getNotificationsFromServer = useNotificationState((s) => s.getNotificationsFromServer);

  const getDiaryEntriesFromServer = useDiaryStore((state) => state.getDiaryEntriesFromServer);

  const getEventsFromServer = useCalendarStore((s) => s.getEventsFromServer);

  const getPointsFromServer = usePesticideMapStore((s) => s.getPointsFromServer);

  const getAudiosFromServer = useHivesSoundsStore((s) => s.getAudiosFromServer);

  const getDevicesFromServer = useDevicesStore((s) => s.getDevicesFromServer);

  const getMercuryDictionaries = useMercuryStore((s) => s.getMercuryDictionaries);
  const getJournalEntries = useMercuryStore((s) => s.getJournalEntries);
  const getTransactionEntries = useMercuryStore((s) => s.getTransactionEntries);

  const getTimezones = useTimezonesStore((s) => s.getTimezones);
  const setUserNowDate = useUserStore((s) => s.setUserNowDate);

  const checkVetisAccess = useVetisStore((s) => s.checkVetisAccess);

  const getToken = useCallback(async () => {
    let code;
    for (const [key, value] of new URLSearchParams(window.location.search).entries()) {
      if (key === 'code') code = value;
    }

    if (!code) return window.open(authorizePath.current, '_self');

    const params = {
      grant_type: 'code',
      scope: 'openid',
      code,
      client_id: 'web',
      redirect_uri: myHost,
      auth_mode: 'cookie',
    };

    const path = `/ecpp/oauth/realms/main/token?${new URLSearchParams(params).toString()}`;
    await axios.post(path, undefined, {
      headers: {
        authorization: `basic ${btoa('web:')}`,
      },
    });

    navigate(lastPathName);
  }, [navigate, lastPathName, myHost]);

  const setTheme = useCallback(() => {
    applyTheme(theme);
  }, [theme]);

  const loadDynamicallyData = useCallback(
    async (apiaryId: string) => {
      if (!apiaryId) return;

      const userInfo = getMyInfo();

      if (userInfo) {
        await getNotificationsFromServer({userId: userInfo.id, status: 'NEW'});
        // await getNotificationsFromServer({apiaryId, userId: userInfo.id, status: 'NEW'});
      }
      await getDictionariesFromServer(apiaryId);
      await getHivesFromServer({apiaryId});
      await getSharedHives();
      await getEventsFromServer({apiaryId});
      await getDevicesFromServer({apiaryId});

      await getTimezones();

      await getDiaryEntriesFromServer({apiaryId});
      const apiary = useApiariesStore.getState().currentApiary();
      const {latitude = 0, longitude = 0} = apiary?.coordinates ?? {};
      const lat = Math.abs(Number(latitude)) <= 90 ? Number(latitude) : 10;
      const lon = Math.abs(Number(longitude)) <= 180 ? Number(longitude) : 10;

      setCoords({lat, lon});
      await fetchForecast({lat, lon});

      await getPointsFromServer(apiaryId);
      await getAudiosFromServer(apiaryId);
      await checkVetisAccess();
    },
    [
      getDictionariesFromServer,
      getHivesFromServer,
      getDiaryEntriesFromServer,
      getMyInfo,
      getNotificationsFromServer,
      getEventsFromServer,
      fetchForecast,
      setCoords,
    ]
  );

  const addYmapsScript = (YANDEX_TOKEN: string) => {
    const ymapsScript = document.querySelector('script[src*="api-maps.yandex"]');
    if (ymapsScript) return;

    const src = `https://api-maps.yandex.ru/v3/?apikey=${YANDEX_TOKEN}&lang=ru_RU`;
    const script = document.createElement('script');

    script.setAttribute('src', src);

    script.onload = () => {
      setTimeout(() => {
        if (!(window as Window & typeof globalThis & {ymaps3: any}).ymaps3)
          console.error('Failed to load Yandex Maps');
      }, 100);
    };

    script.onerror = () => {
      console.error('Failed to load Yandex Maps script');
    };

    setYandexMapsScript(script);

    document.head.appendChild(script);
  };

  const initApp = useCallback(
    async (YANDEX_TOKEN: string) => {
      setTheme();
      addYmapsScript(YANDEX_TOKEN);
      if (
        window.location.pathname.includes('/oauth/realms/main/login') ||
        window.location.pathname.includes('/oauth/realms/main/registration') ||
        window.location.pathname.includes('/oauth/realms/main/recovery-password') ||
        window.location.pathname.includes('/personal-data-consent')
      ) {
        isInitApp.current = true;

        return;
      }

      if (window.location.search.includes('code')) {
        localStorage.setItem('lastPathName', pathname);
        await getToken();
      }

      try {
        await getUserInfo();
      } catch (e: any) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) return;
        throw e;
      }

      const myInfo = getMyInfo();

      await getApiaries(myInfo?.id);
      await getUsers();

      const apiaryId = getCurrentApiaryId();
      if (apiaryId) {
        await loadDynamicallyData(apiaryId);
      }
      await getShareOffers();
      setIAmAdmin();

      isInitApp.current = true;
      setIsInitReturned(true);
      setUserNowDate(getNowForUserTz());
      logUnsupportedBrowser();
    },
    [
      pathname,
      isInitApp,
      getCurrentApiaryId,
      navigate,
      getToken,
      getUserInfo,
      getApiaries,
      getUsers,
      loadDynamicallyData,
      getMyInfo,
      setTheme,
    ]
  );

  useEffect(() => {
    if (!isInitAppStart.current) {
      initApp(config.VITE_YANDEX_TOKEN);
      isInitAppStart.current = true;
    }

    return () => {
      if (yandexMapsScript && yandexMapsScript.parentNode) {
        yandexMapsScript.parentNode.removeChild(yandexMapsScript);
        setYandexMapsScript(null);
      }
    };
  }, [isInitAppStart, initApp, config]);

  useEffect(() => {
    setIsInitReturned(isInitApp.current);
  }, [isInitApp]);

  useEffect(() => {
    if (isInitApp.current) {
      const apiaryId = getCurrentApiaryId();
      if (apiaryId) loadDynamicallyData(apiaryId);
    }
  }, [currentApiary, isInitApp]);

  return isInitReturned;
};
